"use client";

import { useState, useEffect, useCallback } from "react";
import { useChannels } from "../hooks/useChannels";
import { useMessages } from "../hooks/useMessages";
import { useAgents } from "../hooks/useAgents";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useWork } from "../hooks/useWork";
import { useMembers } from "../hooks/useMembers";
import { useUnreads } from "../hooks/useUnreads";
import { useIsMobile } from "../hooks/useIsMobile";
import { useAuth } from "../providers/AuthProvider";
import { useStore } from "../store";
import { useWsStatus, useLastReadEventId, useHasAnyUnreads } from "../store/selectors";
import { useSetFooterStatus } from "./Footer";
import { useHeaderAction } from "./AppHeader";
import { ChatSidebar } from "./ChatSidebar";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import { EmptyState } from "./EmptyState";
import { MembersPanel } from "./MembersPanel";
import { TerminalView } from "./TerminalView";
import { useNotifications } from "../hooks/useNotifications";
import { PushNotificationBanner } from "./PushNotificationBanner";
import { InstallRequiredModal } from "./InstallRequiredModal";

export function ChatLayout() {
  const { token } = useAuth();
  const { channels, loading: channelsLoading } = useChannels();
  const { agents } = useAgents();
  const { user } = useCurrentUser();
  const [selectedChannelId, setSelectedChannelIdRaw] = useState<string | null>(
    null
  );
  // Sync to store immediately (not via useEffect) so the WebSocket dispatch
  // can read it when catch-up events arrive during initial load.
  const setSelectedChannelId = useCallback((id: string | null) => {
    setSelectedChannelIdRaw(id);
    useStore.getState().selectChannel(id);
  }, []);
  // Track whether the channel was explicitly selected by the user (vs auto-selected on load).
  // We only mark a channel as read when the user explicitly picks it or after initial render.
  // Use state (not ref) so the markRead useEffect re-runs when this flips to true.
  const [userHasSelected, setUserHasSelected] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  const [membersPanelOpen, setMembersPanelOpen] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const { messages, loading: messagesLoading, sendMessage, fetchId, hasMore, fetchOlderMessages } =
    useMessages(selectedChannelId);
  const status = useWsStatus();
  const { loadUpdates, sessions: allSessions } =
    useWork(selectedChannelId);
  const { userDisplayNames, agentDisplayNames } = useMembers(selectedChannelId);
  const setFooterStatus = useSetFooterStatus();
  const { register, unregister, updateCollapsed, setChannelName, setToggleMembers, setUnreadBadge } = useHeaderAction();
  const {
    initFromChannels,
    markRead,
    getStatus: getUnreadStatus,
  } = useUnreads();
  const isMobile = useIsMobile();
  const lastReadEventId = useLastReadEventId(selectedChannelId);
  const globalUnreadStatus = useHasAnyUnreads();
  const { state: notifState, subscribe: subscribeNotif } = useNotifications();
  const [installModalOpen, setInstallModalOpen] = useState(false);

  // Build a Map for efficient lookup by sessionId
  const workSessionsMap = new Map(allSessions.map((s) => [s.sessionId, s]));

  // Auto-collapse sidebar on mobile (synced with reactive hook)
  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);

  // Register sidebar toggle with the layout-level header
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((c) => !c);
  }, []);

  useEffect(() => {
    register(sidebarCollapsed, toggleSidebar);
    return () => unregister();
  }, [register, unregister, toggleSidebar]);

  // Keep header in sync with sidebar state
  useEffect(() => {
    updateCollapsed(sidebarCollapsed);
  }, [sidebarCollapsed, updateCollapsed]);

  // Push WS status to the footer
  useEffect(() => {
    setFooterStatus(status);
  }, [status, setFooterStatus]);

  // Initialize unreads from channel list
  useEffect(() => {
    if (channels.length > 0) {
      initFromChannels(channels);
      // Track the greatest event ID for WS catch-up
      for (const ch of channels) {
        if (ch.lastEventId) useStore.getState().setLastEventId(ch.lastEventId);
      }
    }
  }, [channels, initFromChannels]);

  // Auto-select first channel when loaded.
  // Delay marking as read for 3s so the user can see unread badges first.
  useEffect(() => {
    if (channels.length > 0 && selectedChannelId === null) {
      setSelectedChannelId(channels[0].id);
      // After a short delay, start treating it as user-viewed
      const timer = setTimeout(() => {
        setUserHasSelected(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [channels, selectedChannelId]);

  // Listen for notification clicks from the service worker.
  // When a user taps a push notification, the SW posts a message with the
  // channelId — we use it to navigate to the correct channel.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "notification-click" && event.data.channelId) {
        setUserHasSelected(true);
        setSelectedChannelId(event.data.channelId);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [setSelectedChannelId]);

  // Push notification prompting is handled by the PushNotificationBanner
  // component rendered below — no silent auto-subscribe.

  // Work sessions are now auto-initialized in setMessages store action.
  // WebSocket events are handled by lib/websocket.ts → store actions.
  // Work sessions persist across channel switches (globally keyed by sessionId).

  // Mark channel as read when viewing it and when new messages arrive.
  // Only fire when the user explicitly selected the channel (not on
  // auto-select at page load) OR when new messages arrive in the
  // already-viewed channel. This prevents silently clearing unreads
  // for whichever channel happens to be first in the list.
  useEffect(() => {
    if (!selectedChannelId || messages.length === 0 || messagesLoading) return;
    if (!userHasSelected) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.id && lastMsg.channelId === selectedChannelId) {
      markRead(selectedChannelId, lastMsg.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId, messages.length, messagesLoading, fetchId, markRead, userHasSelected]);

  // Sync hamburger badge with global unread status (reactive — re-runs on store change)
  useEffect(() => {
    setUnreadBadge(globalUnreadStatus);
  }, [globalUnreadStatus, setUnreadBadge]);

  const selectedChannel = channels.find((c) => c.id === selectedChannelId);

  // Push selected channel name and members toggle to the header (for mobile display)
  useEffect(() => {
    setChannelName(selectedChannel?.name ?? null);
  }, [selectedChannel?.name, setChannelName]);

  useEffect(() => {
    if (selectedChannel) {
      setToggleMembers(() => () => setMembersPanelOpen((v) => !v));
    } else {
      setToggleMembers(null);
    }
    return () => setToggleMembers(null);
  }, [selectedChannel, setToggleMembers]);

  const handleSelectChannel = (id: string) => {
    setUserHasSelected(true);
    setSelectedChannelId(id);
    setMembersPanelOpen(false);
    setTerminalOpen(false);
    // Auto-collapse on mobile after selecting
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  };

  const isChannelCreator = selectedChannel?.createdBy === user?.id;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        background: "var(--color-bg)",
      }}
    >
      <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden", minWidth: 0 }}>
        <ChatSidebar
          channels={channels}
          selectedId={selectedChannelId}
          onSelect={handleSelectChannel}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
          getUnreadStatus={getUnreadStatus}
        />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            paddingBottom: 28,
          }}
        >
          {channelsLoading && channels.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--color-text-faint)",
                fontSize: "0.8rem",
              }}
            >
              Loading...
            </div>
          ) : channels.length === 0 ? (
            <EmptyState hasAgents={agents.length > 0} />
          ) : !selectedChannel ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--color-text-faintest)",
                fontSize: "0.85rem",
              }}
            >
              Select a channel
            </div>
          ) : (
            <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {terminalOpen ? (
                <TerminalView
                  channelId={selectedChannelId!}
                  onClose={() => setTerminalOpen(false)}
                />
              ) : (
              <>
              <div style={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <MessageList
                  messages={messages}
                  loading={messagesLoading}
                  channelId={selectedChannelId}
                  currentUserId={user?.id ?? null}
                  currentUserDisplayName={user?.displayName ?? null}
                  agents={agents}
                  userDisplayNames={userDisplayNames}
                  agentDisplayNames={agentDisplayNames}
                  workSessions={workSessionsMap}
                  onLoadWorkUpdates={loadUpdates}
                  hideChannelHeader
                  lastReadEventId={userHasSelected ? lastReadEventId : null}
                  hasMore={hasMore}
                  onLoadMore={fetchOlderMessages}
                />
              </div>
              <MessageInput onSend={sendMessage} token={token} />
              </>
              )}
            </div>
            {membersPanelOpen && (
              <MembersPanel
                channelId={selectedChannel.id}
                channelName={selectedChannel.name}
                isCreator={!!isChannelCreator}
                onClose={() => setMembersPanelOpen(false)}
                onOpenTerminal={() => { setTerminalOpen(true); setMembersPanelOpen(false); }}
              />
            )}
            </div>
          )}
        </div>
      </div>
      {/* Push notification opt-in banner — shown before the system permission dialog */}
      <PushNotificationBanner
        state={notifState}
        onSubscribe={subscribeNotif}
        onInstallRequired={() => setInstallModalOpen(true)}
      />
      {/* iOS install-required modal — shown when iOS Safari user taps Enable */}
      <InstallRequiredModal
        open={installModalOpen}
        onClose={() => setInstallModalOpen(false)}
      />
    </div>
  );
}
